/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as moment from "moment-timezone";
import * as $ from "jquery";
import {UiComponentConfig} from "../generated/UiComponentConfig";
import {UiWindow, UiWindowListener} from "./UiWindow";
import {UiConfigurationConfig} from "../generated/UiConfigurationConfig";
import {UiWindowConfig} from "../generated/UiWindowConfig";
import {UiNotificationConfig} from "../generated/UiNotificationConfig";
import {UiComponent} from "./UiComponent";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {convertJavaDateTimeFormatToMomentDateTimeFormat, exitFullScreen, showNotification} from "./Common";
import {UiRootPanelCommandHandler, UiRootPanelConfig} from "../generated/UiRootPanelConfig";
import {UiComponentRevealAnimation} from "../generated/UiComponentRevealAnimation";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";
import {UiTemplateConfig} from "../generated/UiTemplateConfig";
import * as log from "loglevel";
import {ElementUiComponentAdapter} from "./micro-components/ElementUiComponentAdapter";
import {UiGenericErrorMessageOption} from "../generated/UiGenericErrorMessageOption";

require("moment-jdateformatparser");


interface ChildComponent {
	$wrapper: JQuery,
	component: UiComponent<UiComponentConfig>
}

/**
 * @author Yann Massard (yamass@gmail.com)
 */
export class UiRootPanel extends UiComponent<UiRootPanelConfig> implements UiRootPanelCommandHandler {

	private static LOGGER: log.Logger = log.getLogger("UiRootPanel");
	private static ALL_ROOT_PANELS_BY_ID: { [id: string]: UiRootPanel } = {};
	private static BACKGROUND_IMAGES_BY_ID: {
		[index: string]: {
			image: string;
			blurredImage: string;
		}
	} = {};
	private static WINDOWS_BY_ID: { [windowId: string]: UiWindow } = {};

	private $root: JQuery;
	private childComponentsById: {[id: string]: ChildComponent} = {};
	private visibleChildComponent: ChildComponent;
	private $backgroundTransitionStyle: JQuery;
	private $backgroundStyle: JQuery;
	private $imagePreloadDiv: JQuery;

	static __initialize() {
		$(window).resize((e) => {
			this.ALL_ROOT_PANELS.forEach(rootPanel => {
				rootPanel.reLayout();
			});
			Object.keys(this.WINDOWS_BY_ID).map(id => this.WINDOWS_BY_ID[id]).forEach(window => window.reLayout());
		});
	}

	constructor(config: UiRootPanelConfig, context: TeamAppsUiContext) {
		super(config, context);
		UiRootPanel.ALL_ROOT_PANELS_BY_ID[config.id] = this;
		
		this.$root = $(`<div data-background-container-id="${config.id}" class="UiRootPanel teamapps-backgroundImage">
              <div class="image-preload-div"></div>
              <style data-style-type="backgroundTransitionStyle"></style>
              <style data-style-type="backgroundStyle"></style>
              <style></style>
		</div>`);
		this.$imagePreloadDiv = this.$root.find(".image-preload-div");
		this.$backgroundTransitionStyle = this.$root.find("[data-style-type='backgroundTransitionStyle']");
		this.$backgroundStyle = this.$root.find("[data-style-type='backgroundStyle']");

		if (config.childComponents != null && config.childComponents.length > 0) {
			config.childComponents.forEach(c => this.addChildComponent(c, false));
			this.setVisibleChildComponent(config.visibleChildComponentId || config.childComponents[0].id, null, 0);
		}
	}

	public getMainDomElement(): JQuery {
		return this.$root;
	}

	public addChildComponent(componentConfig: UiComponentConfig, show: boolean) {
		let uiComponent = componentConfig ? this._context.createComponent(componentConfig) : null;
		this.addChildComponentInstance(uiComponent, show);
	}

	public addChildComponentInstance(component: UiComponent<UiComponentConfig>, show: boolean) {
		let $childComponentContainer = $(`<div class="child-component-wrapper ${show ? 'active' : ''}">`)
			.appendTo(this.$root);
		component.getMainDomElement().appendTo($childComponentContainer);
		this.childComponentsById[component.getId()] = {
			component: component,
			$wrapper: $childComponentContainer
		};
		component.attachedToDom = this.attachedToDom;
		if (show) {
			this.setVisibleChildComponent(component.getId(), null, 0);
		}
	}

	public setVisibleChildComponent(childComponentId: string, animation: UiComponentRevealAnimation | null, animationDuration: number): void {
		if (!childComponentId) {
			return;
		}
		let childComponent = this.childComponentsById[childComponentId];
		if (childComponent) {
			this.visibleChildComponent = childComponent;
			this.childComponents.forEach(c => c.$wrapper.removeClass('active'));
			childComponent.$wrapper.addClass('active');
			this.visibleChildComponent.component.reLayout();
		}
	}

	public removeChildComponent(childComponentId: string): void {
		let childComponent = this.childComponentsById[childComponentId];
		delete this.childComponentsById[childComponentId];
		if (childComponent) {
			childComponent.component.destroy();
			childComponent.$wrapper.remove();
		}
	}

	private get childComponents() {
		return Object.keys(this.childComponentsById).map(id => this.childComponentsById[id]);
	}

	public static setConfig(config: UiConfigurationConfig, context: TeamAppsUiContext) {
		let oldConfig = context.config;
		if (!oldConfig || oldConfig.isoLanguage !== config.isoLanguage) {
			$.getScript("static/runtime-resources/moment-locales/" + config.isoLanguage + ".js");
			$.getScript("static/runtime-resources/fullcalendar-locales/" + config.isoLanguage + ".js");
		}
		moment.locale(config.isoLanguage);
		config.dateFormat = convertJavaDateTimeFormatToMomentDateTimeFormat(config.dateFormat);
		config.timeFormat = convertJavaDateTimeFormatToMomentDateTimeFormat(config.timeFormat);
		this.setTheme(config.theme);

		this.ALL_ROOT_PANELS.forEach(uiRootPanel => {
			uiRootPanel.$root.toggleClass("optimized-for-touch", config.optimizedForTouch);
		});

		this.LOGGER.warn("TODO Setting configuration on context. This should be implemented using an event instead!");
		(context as any).config = config; // TODO change this to firing an event to the context!!!!
	}

	private static get ALL_ROOT_PANELS() {
		return Object.keys(this.ALL_ROOT_PANELS_BY_ID).map(id => this.ALL_ROOT_PANELS_BY_ID[id]);
	}

	public static registerBackgroundImage(id: string, image: string, blurredImage: string) {
		if (!id) {
			this.LOGGER.error(`Cannot register background image. Missing id!`);
			return;
		}
		if (!image) {
			this.LOGGER.warn(`Missing background image for image registration! (Still registering...)`);
		}
		if (!blurredImage) {
			blurredImage = image;
		}
		
		(new Image()).src = image; // preload
		(new Image()).src = blurredImage; // preload

		this.BACKGROUND_IMAGES_BY_ID[id] = {image, blurredImage};
	}

	public static setBackgroundImage(id: string, animationDuration: number) {
		let registeredImage = this.BACKGROUND_IMAGES_BY_ID[id];
		if (!registeredImage) {
			this.LOGGER.warn(`Background image with id ${id} does not exist!`);
			return;
		}
		this.ALL_ROOT_PANELS.forEach(uiRootPanel => {
			uiRootPanel._setBackgroundImage(registeredImage.image, registeredImage.blurredImage, animationDuration);
		})
	}

	private _setBackgroundImage(image: string, blurredImage: string, animationDuration: number) {
		this.$backgroundTransitionStyle.text(`
                [data-background-container-id='${this.getId()}'] .teamapps-backgroundImage,
                [data-background-container-id='${this.getId()}'] .teamapps-blurredBackgroundImage {
                    transition: background-image ${animationDuration}ms ease-in-out;
                }
            `);
		this.$root[0].clientWidth; // ensure the css is applied!
		this.$backgroundStyle.text(`
				[data-background-container-id='${this.getId()}'].teamapps-backgroundImage,
                [data-background-container-id='${this.getId()}'] .teamapps-backgroundImage {
                    background-image: ${image ? `url(${image})` : 'none'};
                }
                [data-background-container-id='${this.getId()}'].teamapps-blurredBackgroundImage,
                [data-background-container-id='${this.getId()}'] .teamapps-blurredBackgroundImage {
                    background-image: ${blurredImage ? `url(${blurredImage})` : 'none'};
                }
            `);
	}

	public static showWindow(windowConfig: UiWindowConfig, animationDuration = 1000, context: TeamAppsUiContext) {
		let uiWindow = context.createComponent(windowConfig) as UiWindow;
		this.showWindowComponent(uiWindow, animationDuration);
	}

	public static showWindowComponent(uiWindow: UiWindow, animationDuration = 1000) {
		this.ALL_ROOT_PANELS.forEach(rootPanel => {
			rootPanel.childComponents.forEach(childComponent => {
				rootPanel.visibleChildComponent.$wrapper.css({
					transition: `opacity ${animationDuration}ms, filter ${animationDuration}ms`
				});
			});

		});

		uiWindow.getMainDomElement().appendTo(document.body);
		uiWindow.getMainDomElement().attr("data-background-container-id", this.ALL_ROOT_PANELS[0].getId());
		uiWindow.attachedToDom = true;
		uiWindow.setListener({
			onWindowClosed: (window, animationDuration) => this.closeWindow(window.getId(), animationDuration)
		});
		this.WINDOWS_BY_ID[uiWindow.getId()] = uiWindow;
		uiWindow.show(animationDuration);

		this.ALL_ROOT_PANELS.forEach(rootPanel => {
			rootPanel.getMainDomElement().toggleClass("modal-window-mode", uiWindow.isModal());
		});
	}

	public static closeWindow(windowId: string, animationDuration: number) {
		this.ALL_ROOT_PANELS.forEach(rootPanel => {
			rootPanel.getMainDomElement().removeClass('modal-window-mode');
		});

		let uiWindow = this.WINDOWS_BY_ID[windowId];
		uiWindow.hide(animationDuration);
		delete this.WINDOWS_BY_ID[windowId];

		setTimeout(() => {
			uiWindow.destroy();
			uiWindow.getMainDomElement().remove();
		}, animationDuration);
	};

	onResize() {
		if (this.visibleChildComponent) {
			this.visibleChildComponent.component.reLayout();
		}
	}

	protected onAttachedToDom() {
		this.childComponents.forEach(c => c.component.attachedToDom = true);
	}

	public destroy(): void {
		this.childComponents.forEach(c => c.component.destroy());
		delete UiRootPanel.ALL_ROOT_PANELS_BY_ID[this.getId()];
		if (Object.keys(UiRootPanel.ALL_ROOT_PANELS_BY_ID).length === 0) {
			Object.keys(UiRootPanel.WINDOWS_BY_ID).forEach(windowId => UiRootPanel.WINDOWS_BY_ID[windowId].destroy());
		}
	}

	public static buildRootPanel(containerElementId: string, uiRootPanel: UiRootPanelConfig, context: TeamAppsUiContext): void {
		const $container = $(containerElementId ? "#" + containerElementId : document.body);
		let component = context.createComponent(uiRootPanel);
		component.getMainDomElement().appendTo($container);
		component.attachedToDom = true;
	}

	public static setTheme(theme: string) {
		this.ALL_ROOT_PANELS.forEach(uiRootPanel => {
			uiRootPanel.$root[0].className = uiRootPanel.$root[0].className.replace(/theme-\w+/, '');
			if (theme) {
				uiRootPanel.$root.addClass("theme-" + theme);
			}
		})
	}

	public static showNotification(notification: UiNotificationConfig, context: TeamAppsUiContext) {
		showNotification(context.templateRegistry.createTemplateRenderer(notification.template).render(notification.data), notification);
	}

	public static downloadFile(fileUrl: string, fileName: string) {
		const link = document.createElement('a');
		link.href = fileUrl + (fileUrl.indexOf('?') === -1 ? '?' : '&') + 'teamapps-download-filename=' + fileName;
		(<any>link).download = fileName;
		if (document.createEvent) {
			const e = document.createEvent('MouseEvents');
			e.initEvent('click', true, true);
			link.dispatchEvent(e);
			return true;
		}
	}

	public static registerTemplate(name: string, template: UiTemplateConfig, context: TeamAppsUiContext) {
		context.templateRegistry.registerTemplate(name, template);
	}

	public static registerTemplates(templates: { [name: string]: UiTemplateConfig }, context: TeamAppsUiContext) {
		Object.keys(templates).forEach(templateName => {
			this.registerTemplate(templateName, templates[templateName], context);
		});
	}

	public static addClientToken(token: string) {
		let tokens = UiRootPanel.loadClientTokens();
		tokens[token] = true;
		UiRootPanel.saveClientTokens(tokens);
	}

	public static removeClientToken(token: string) {
		let tokens = UiRootPanel.loadClientTokens();
		delete tokens[token];
		UiRootPanel.saveClientTokens(tokens);
	}

	public static clearClientTokens() {
		UiRootPanel.saveClientTokens({});
	}

	private static loadClientTokens(): { [tokenName: string]: true } {
		let tokens: { [tokenName: string]: true } = {};
		let tokenJson = localStorage.getItem("clientTokens");
		if (tokenJson) {
			tokens = JSON.parse(tokenJson);
		}
		return tokens;
	}

	public static getClientTokens(): string[] {
		return Object.keys(UiRootPanel.loadClientTokens());
	}

	private static saveClientTokens(tokens: { [token: string]: true }) {
		localStorage.setItem("clientTokens", JSON.stringify(tokens));
	}

	public static exitFullScreen() {
		exitFullScreen();
	}

	public static showGenericErrorMessage(title: string, message: string, options: UiGenericErrorMessageOption[], context: TeamAppsUiContext): void {
		let uiWindow = new UiWindow({
			id: null,
			title: title,
			width: 330,
			height: 150,
			modalBackgroundDimmingColor: {red: 0, green: 0, blue: 0, alpha: .5},
			headerBackgroundColor: {red: 255, green: 255, blue: 255, alpha: .8},
			bodyBackgroundColor: {red: 255, green: 255, blue: 255, alpha: .7},
			modal: true,
			content: null
		}, context);
		let $contentElement = $(`<div class="UiGenericErrorMessage">
	<div class="icon img img-48" style="background-image: url(/static/resources/window-close-grey.png)"></div>
	<div class="message">${message}</div>
	<div class="option-buttons">
		${options.map(o => `<div class="btn btn-default ${UiGenericErrorMessageOption[o].toLowerCase()}">${UiGenericErrorMessageOption[o]}</div>`).join("")}
	</div>
</div>`);
		uiWindow.setContentComponent(new ElementUiComponentAdapter($contentElement));
		$contentElement.find('.ok').on('click', () => {
			uiWindow.close(500);
		});
		$contentElement.find('.reload').on('click', () => {
			window.location.reload(true);
		});
		UiRootPanel.showWindowComponent(uiWindow, 500);
	}
}

UiRootPanel.__initialize();

TeamAppsUiComponentRegistry.registerComponentClass("UiRootPanel", UiRootPanel);